import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from 'axios';

// ---------------------------------------------------------------------------
// Extraer ID del video de TikTok.
// ---------------------------------------------------------------------------
const tiktokId = (url) => {
  if (!url) return null;
  return (url.match(/video\/(\d+)/) || [])[1];
};

// ---------------------------------------------------------------------------
// TikTok dibuja sus botones con tamaño fijo, por eso en un recuadro chico
// (celular) se ven enormes. Solución: el reproductor se dibuja a un tamaño
// intermedio (BASE_W x BASE_H) y se reduce con CSS hasta que quepa en la tarjeta.
// Más grande BASE_W = botones más chicos. Más chico BASE_W = botones más grandes.
// ---------------------------------------------------------------------------
const BASE_W = 230; // 325 = botones muy chicos, 230 = medianos, 150 = grandes
const BASE_H = Math.round((BASE_W * 16) / 9); // siempre formato vertical 9:16

const ScaledTikTok = ({ src, title }) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scale = width ? Math.min(1, width / BASE_W) : 1;

  return (
    <div
      ref={ref}
      style={{
        position: "relative",
        width: "100%",
        height: width ? Math.round(BASE_H * scale) : 200,
        overflow: "hidden",
        backgroundColor: "#000",
      }}
    >
      {width > 0 && (
        <iframe
          title={title}
          src={src}
          allow="fullscreen"
          allowFullScreen
          style={{
            position: "absolute",
            top: 0,
            left: Math.round((width - BASE_W * scale) / 2),
            width: BASE_W,
            height: BASE_H,
            border: 0,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Videos e imágenes de productos.
// ---------------------------------------------------------------------------
const ProductMedia = ({ product, isMobile }) => {
  const videoId = tiktokId(product.video_url);

  const hidePhoto =
    product.category === "Espejos Manuales a Retractiles" &&
    !!videoId;

  // Fotografía del producto.
  const photo = (
    <div
      className="product-image"
      style={{
        padding: isMobile ? "10px" : "16px",
        backgroundColor: "#FEFCF9",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: isMobile ? "120px" : "210px",
      }}
    >
      <img
        src={product.image}
        className="img-fluid"
        alt={product.name}
        style={{
          maxHeight: "100%",
          maxWidth: "100%",
          width: "auto",
          height: "auto",
          objectFit: "contain",
        }}
      />
    </div>
  );

  // Si no cumple ambas condiciones, mostrar solamente la fotografía.
  if (!hidePhoto) {
    return photo;
  }

  const videoSrc = `https://www.tiktok.com/player/v1/${videoId}?autoplay=0&loop=1&controls=1&music_info=0&description=0&rel=0&closed_caption=0`;

  // Celular: video reducido para que los botones de TikTok no se vean enormes.
  if (isMobile) {
    return (
      <div className="product-video" style={{ width: "100%", backgroundColor: "#000" }}>
        <ScaledTikTok src={videoSrc} title={`Video de ${product.name}`} />
      </div>
    );
  }

  // Desktop: igual que antes.
  return (
    <div
      className="product-video"
      style={{
        width: "100%",
        height: "200px",
        backgroundColor: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <iframe
        title={`Video de ${product.name}`}
        src={videoSrc}
        allow="fullscreen"
        allowFullScreen
        style={{
          width: "100%",
          height: "100%",
          border: 0,
          display: "block",
        }}
      />
    </div>
  );
};

const ProductCatalog = () => {
  const { categoria } = useParams();
  const location = useLocation();
  const [allProducts, setAllProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const navigate = useNavigate();
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [banners, setBanners] = useState([]);
  const [subcategorys, setSubCategorys] = useState([]);
  const isMobile = window.innerWidth <= 768;
  const postApi = "https://ryc-soluciones-nicaragua.com/api/index.php";
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Camaras Vehiculos');
  const isVideoFilter =(categoria || selectedCategory).trim().replaceAll("-", " ").toLowerCase() ==="espejos manuales a retractiles";
  const formatPrice = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "C$ 0";
    const formatted = new Intl.NumberFormat("es-NI", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
    return `C$ ${formatted}`;
  };


  const queryParams = new URLSearchParams(location.search);
  const searchQuery = queryParams.get("search");

  const getImageSize = () => {
    const width = window.innerWidth;
    if (width <= 768) return 220;
    else if (width <= 1024) return 260;
    else return 340;
  };

  const goToBanner = useCallback((index) => {
    setCurrentBannerIndex(index);
  }, []);

  const prevBanner = useCallback(() => {
    setCurrentBannerIndex((prev) => (prev - 1 + banners.length) % banners.length);
  }, [banners.length]);

  const nextBanner = useCallback(() => {
    setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
  }, [banners.length]);

  const fetchProducts = async () => {
    try {
      let url = `${postApi}?getAllProductsCam=true`;
      if (categoria) {
        setSelectedCategory(categoria.replaceAll("-", " "));

        url = `${postApi}?getAllProductsByCategory=true&category=${categoria.replaceAll("-", " ")}`;
        setSelectedFilters([]);
        setShowFilters(false);
      } else if (searchQuery) {
        setSelectedCategory("");
        url = `${postApi}?searchProduct=true&query=${encodeURIComponent(searchQuery)}`;
      }

      const response = await axios.get(url);

      const filteredProducts = response.data.filter(
        (product) => product.category?.trim().toLowerCase() !== "restaurante"
        &&
        product.category?.trim().toLowerCase() !== "skincare"
      );

      setProducts(filteredProducts);
      setAllProducts(filteredProducts);
    } catch (error) {
      console.error("Error al obtener productos:", error);
    }
  };

  // Obtener productos desde la API
  useEffect(() => {
    
    const fetchBanners = async () => {
      try {
        const response = await axios.get(`${postApi}?getAllBanners=true`);
        setBanners(
          response.data.filter((banner) => banner.type == "Otros")
        );
      } catch (error) {
        console.error("Error al obtener banners:", error);
      }
    };

    const fetchSubCategorys = async () => {
      try {
        const categoriaFinal = categoria?.trim()
      ? categoria.replaceAll("-", " ")
      : "Camaras Vehiculos";
        const response = await axios.get(`${postApi}?getAllsubcategory=true&category=${categoriaFinal.replaceAll("-", " ")}`);
        setSubCategorys(response.data);
      } catch (error) {
        console.error("Error al obtener banners:", error);
      }
    };

    fetchBanners();
    fetchProducts();
    fetchSubCategorys();
  }, [categoria, searchQuery]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBannerIndex((prevIndex) => (prevIndex + 1) % banners.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [banners]);

  const handleFilterChange = (category, keyword) => {
    setSelectedFilters(prev => {
      const existingCategory = prev.find(item => item.category === category);
      if (existingCategory) {
        // Si ya existe la categoría, añadir o quitar keyword
        const keywordExists = existingCategory.keywords.includes(keyword);
        const newKeywords = keywordExists
          ? existingCategory.keywords.filter(k => k !== keyword)
          : [...existingCategory.keywords, keyword];

        // Si no quedan keywords, quitar toda la categoría
        if (newKeywords.length === 0) {
          return prev.filter(item => item.category !== category);
        }

        return prev.map(item =>
          item.category === category ? { ...item, keywords: newKeywords } : item
        );
      } else {
        // Si no existe la categoría, agregarla
        return [...prev, { category, keywords: [keyword] }];
      }
    });
  };

  const applyFilters = async () => {

  if (selectedFilters.length === 0) {
    console.log("No hay filtros seleccionados, no se hace petición.");
    return;
  }

  try {
    
    const filtersPayload = JSON.stringify(selectedFilters);

    console.log("Filtros enviados al PHP:", filtersPayload);
    setProducts([]);
    const url = `${postApi}?getProductsByFilter=true&filters=${encodeURIComponent(filtersPayload)}`;
    const response = await axios.get(url);
    setProducts(response.data);
    setShowFilters(false);

  } catch (error) {
    console.error("Error aplicando filtro:", error);
  }
};


  const clearFilters = () => {
    setSelectedFilters([]);
    fetchProducts();
    setShowFilters(false);
  };

  const buttonBase = {
  border: "none",
  borderRadius: "25px",
  fontWeight: 600,
  textTransform: "uppercase",
  fontSize: isMobile ? "0.7rem" : "0.8rem",
  letterSpacing: "0.5px",
  transition: "all 0.3s ease",
  cursor: "pointer",
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textDecoration: "none",
  whiteSpace: "nowrap",
};

const hasMobileBanners =
  isMobile &&
  banners.length > 0 &&
  banners.every(
    (b) => b.image_url_mobile && b.image_url_mobile !== b.image_url_desktop
  );

  return (
    
    <div className="mb-4">
<style>{`
/* Capa azul "cine" sobre la imagen */
.product-card .product-image {
  position: relative;
  overflow: hidden;
}
.product-card .product-image::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 55, 123, 0.15) 0%,
    rgba(0, 43, 92, 0.45) 100%
  );
  opacity: 0;
  transition: opacity 0.4s ease;
  pointer-events: none;
}
  .product-card {
    transition: transform 0.35s ease, box-shadow 0.35s ease;
  }

  /* Línea azul arriba de la tarjeta: crece desde el centro */
  .product-card::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: #00b5e2;
    transform: scaleX(0);
    transition: transform 0.35s ease;
    z-index: 2;
  }

  .product-card .product-name {
    color: #111111;
    transition: color 0.3s ease;
  }

  /* Botón Ver detalles */
  .product-card .btn-details {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background-color: #f4f5f7;
    color: #00377b;
    border: 1px solid #d5d9e0;
    border-radius: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease;
  }

  /* Botón WhatsApp */
  .product-card .btn-whatsapp {
    background-color: #25D366;
    transition: background-color 0.3s ease;
  }

  /* Solo en dispositivos con mouse (en el celular el hover se queda pegado) */
  @media (hover: hover) {
  .product-card:hover .product-image::after { opacity: 1; }
    .product-card:hover {
      transform: scale(1.05);
      box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2);
    }
    .product-card:hover::before { transform: scaleX(1); }

    /* Nombre: azul más suave */
    .product-card:hover .product-name { color: #2f6fc4; }

    /* Ver detalles: al pasar por la tarjeta se refuerza */
    .product-card:hover .btn-details {
      background-color: #e8effa;
      border-color: #00377b;
    }

    /* Ver detalles: al pasar justo sobre el botón se llena de azul */
    .product-card .btn-details:hover {
      background-color: #00377b;
      border-color: #00377b;
      color: #fff;
    }

    /* WhatsApp: verde más fuerte */
    .product-card:hover .btn-whatsapp { background-color: #1aa851; }
  }
`}</style>
     

      {/* Botón para abrir filtros */}
      {/* {subcategorys.some(sub => sub.category_name === selectedCategory) && ( */}
       {/*  <button className="btn btn-primary mb-3" onClick={() => setShowFilters(true)}>
          <i className="fas fa-filter"></i> Filtros
        </button> */}
      {/* )} */}
     {/* Banner */}
{banners.length > 0 && (
  <div
    style={{
      position: "relative",
      overflow: "hidden",
      borderRadius: isMobile ? "24px" : "20px",
      margin: isMobile ? "0 -12px" : "20px 24px 0",
      boxShadow: "0 8px 32px rgba(27,42,74,0.12)",
      backgroundColor: "#FFFFFF",
    }}
  >
    {/* Slides con fade */}
<div
  style={{
    position: "relative",
    width: "100%",
    aspectRatio: hasMobileBanners ? "6 / 5" : "auto",
    minHeight: isMobile ? 0 : "200px",
  }}
>
  {banners.map((banner, index) => (
    <img
      key={index}
      src={
        isMobile && banner.image_url_mobile
          ? banner.image_url_mobile
          : banner.image_url_desktop
      }
      alt={`Banner ${index + 1}`}
      style={{
        position: hasMobileBanners ? "absolute" : index === 0 ? "relative" : "absolute",
        top: 0,
        left: 0,
        display: "block",
        width: "100%",
        height: hasMobileBanners ? "100%" : "auto",
        maxWidth: "100%",
        objectFit: hasMobileBanners ? "cover" : "contain",
        transition: "opacity 0.7s ease-in-out",
        opacity: currentBannerIndex === index ? 1 : 0,
        pointerEvents: currentBannerIndex === index ? "auto" : "none",
      }}
    />
  ))}
</div>
 
          {/* Botón anterior */}
          <button
            onClick={prevBanner}
            aria-label="Banner anterior"
            style={{
              position: "absolute",
              top: "50%",
              left: isMobile ? "8px" : "16px",
              transform: "translateY(-50%)",
              background: "rgba(0,0,0,0.35)",
              backdropFilter: "blur(4px)",
              border: "none",
              borderRadius: "50%",
              width: isMobile ? "36px" : "44px",
              height: isMobile ? "36px" : "44px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "white",
              fontSize: isMobile ? "16px" : "20px",
              transition: "background 0.3s ease",
              zIndex: 2,
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(0,0,0,0.55)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "rgba(0,0,0,0.35)"}
          >
            <i className="fas fa-chevron-left"></i>
          </button>
 
          {/* Botón siguiente */}
          <button
            onClick={nextBanner}
            aria-label="Banner siguiente"
            style={{
              position: "absolute",
              top: "50%",
              right: isMobile ? "8px" : "16px",
              transform: "translateY(-50%)",
              background: "rgba(0,0,0,0.35)",
              backdropFilter: "blur(4px)",
              border: "none",
              borderRadius: "50%",
              width: isMobile ? "36px" : "44px",
              height: isMobile ? "36px" : "44px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "white",
              fontSize: isMobile ? "16px" : "20px",
              transition: "background 0.3s ease",
              zIndex: 2,
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(0,0,0,0.55)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "rgba(0,0,0,0.35)"}
          >
            <i className="fas fa-chevron-right"></i>
          </button>
 
          {/* Indicadores de puntos */}
          <div
            style={{
              position: "absolute",
              bottom: isMobile ? "10px" : "16px",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: "8px",
              zIndex: 2,
            }}
          >
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => goToBanner(index)}
                aria-label={`Ir al banner ${index + 1}`}
                style={{
                  width: currentBannerIndex === index ? "24px" : "10px",
                  height: "10px",
                  borderRadius: "5px",
                  border: "none",
                  cursor: "pointer",
                  background: currentBannerIndex === index ? "#00b5e2" : "rgba(255,255,255,0.6)",
                  transition: "all 0.3s ease",
                  padding: 0,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                }}
              />
            ))}
          </div>
        </div>
      )}
      {/* Modal de filtros */}
      {showFilters && (
        <div className="modal fade show" style={{ display: "block" }} aria-labelledby="exampleModalLabel" aria-hidden="true">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="exampleModalLabel">Filtrar por Palabras Clave</h5>
                <button type="button" className="btn-close" onClick={() => setShowFilters(false)} aria-label="Close"></button>
              </div>
              <div className="modal-body">
                {/* Recorrer subcategorías y mostrar keywords */}
                {subcategorys.map((sub, index) => (
                  <div key={index}>
                    <strong>{sub.category_name}</strong>
                    {sub.keywords.map((keyword, i) => (
                      <div key={i} className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`filter-${index}-${i}`}
                          checked={selectedFilters.some(f => f.category === sub.category_name && f.keywords.includes(keyword))}
                          onChange={() => handleFilterChange(sub.category_name, keyword)}
                        />
                        <label className="form-check-label" htmlFor={`filter-${index}-${i}`}>
                          {keyword}
                        </label>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-primary" onClick={applyFilters}>Aplicar Filtro</button>
                <button type="button" className="btn btn-danger" onClick={clearFilters}>Borrar Filtros</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* <h2 className="text-center mb-4">{categoria ? `Categoría: ${categoria}` : "Todos los productos"}</h2> */}
      <div
        className="container-fluid"
        style={{ padding: isMobile ? "0 8px 30px" : "0 24px 40px", maxWidth: "1600px", margin: "0 auto" }}
      >
      <div className="m-4 row mt-2" style={{ justifyContent: "center" }}>
        {products.map((product) => (
          <div
              className={`${
                isVideoFilter
                  ? "col-6 col-sm-6 col-md-4 col-lg-3 col-xl-3"
                  : "col-12 col-md-4 col-lg-3 col-xl-3"
              } ${isMobile ? "mb-3 px-1" : "mb-4"}`}
              key={product.id}
            >
            <div
            className="product-card"
              style={{
                  backgroundColor: "white",
                  borderRadius: "16px",
                  overflow: "hidden",
                  transition: "all 0.35s ease",
                  border: "1px solid #e4e3e1",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  cursor: "pointer",
                  position: "relative",
                }}
              /* onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.05)";
                e.currentTarget.style.boxShadow = "0 8px 15px rgba(0, 0, 0, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "none";
              }} */
            >
          
                {/* <div className="product-image" style={{
                    padding: isMobile ? "10px" : "16px",
                    backgroundColor: "#FEFCF9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: isMobile ? "120px" : "210px",
                  }}> */}
                  <ProductMedia product={product} isMobile={isMobile} />
                    {/* <img
                        src={product.image}
                        className="img-fluid"
                        alt={product.name}
                        style={{
                          maxHeight: "100%",
                          maxWidth: "100%",
                          width: "auto",
                          height: "auto",
                          objectFit: "contain",
                        }}
                      />*/}
                {/* </div> */}
                {/* Información a la derecha */}
                <div style={{ padding: isMobile ? "12px 14px" : "16px 20px", flexGrow: 1 }}>
                  <div
                    style={{
                      textTransform: "uppercase",
                      fontSize: "0.9rem",
                      color: "#1B2A4A",
                      fontWeight: 1000,
                      letterSpacing: "1px",
                      marginBottom: "6px",
                    }}
                  >
                    {product.code}
                  </div>
                  <h6 className="product-name" style={{
                      textTransform: "uppercase",
                      fontSize: "0.9rem",
                      // color: "#00377b",
                      fontWeight: 1000,
                      letterSpacing: "1px",
                      marginBottom: "6px",
                    }}>
                    {product.name}
                  </h6>
                  <div
                    style={{
                      fontSize: isMobile ? "0.9rem" : "0.95rem",
                      color: "#5b6b85",
                      fontWeight: 400,
                      lineHeight: 1.45,
                      margin: "0 0 10px",
                    }}
                  >
                    {/* {product.feature} */}
                  </div>
                  {/* <p className="mb-1 is-size-10">Precio:</p> */}
                  {/* <div style={{ color: "#00377b", fontWeight: 700, fontSize: isMobile ? "1.05rem" : "1.2rem" }}>
                    <span
                      style={{
                        textDecoration: product.on_clearance === 1 ? "line-through" : "none",
                      }}
                    >
                      {formatPrice(product.price)}
                    </span>

                    {product.on_clearance === 1 && (
                      <span
                        style={{
                          display: "block",
                          color: "red",
                          fontSize: "1.1rem",
                          fontWeight: 700,
                          marginTop: "2px",
                        }}
                      >
                        {formatPrice(product.clearance_price)}
                      </span>
                    )}
                  </div> */}
                </div>

              {/* Botones debajo de la imagen */}
<div
  className="p-2"
  style={{
    display: "flex",
    flexDirection: isVideoFilter && isMobile ? "column" : "row",
    alignItems: "stretch",
    gap: "8px",
    width: "100%",
  }}
>
 <button
  className="btn-details"
  onClick={() => navigate(`/producto/${product.id}`)}
  style={{
    flex: 1,
    padding: isMobile ? "9px 12px" : "10px 16px",
    fontSize: isMobile ? "0.7rem" : "0.8rem",
  }}
>
  <i className="fa-solid fa-eye"></i>
  Ver detalles
</button>

  <a
  className="btn-whatsapp"
    href={(() => {
                  const phone = "50577005446";
                  //const text = `Hola+estoy+interesado+en+este+producto:%0D%0A%0D%0A*${encodeURIComponent(product.name)}*%0A*Precio:*%20${encodeURIComponent(product.price)}%0A*URL:*%20https%3A%2F%2Fryc-soluciones-nicaragua.com%2Fproducto%2F${encodeURIComponent(product.id)}`;
                  const text = `Hola+estoy+interesado+en+este+producto:%0D%0A%0D%0A*${encodeURIComponent(product.name)}*%0A*URL:*%20https%3A%2F%2Fryc-soluciones-nicaragua.com%2Fproducto%2F${encodeURIComponent(product.id)}`;
                  // Detectar si el usuario está en móvil
                  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

                  return isMobile
                    ? `https://wa.me/${phone}?text=${text}`
                    : `https://web.whatsapp.com/send?phone=${phone}&text=${text}`;
                })()}
    target="_blank"
    rel="noopener noreferrer"
    title={`Hablar sobre ${product.name} en WhatsApp`}
    aria-label="Consultar por WhatsApp"
    style={{
      ...buttonBase,
      // backgroundColor: "#25D366",
      gap: "8px",
      // Móvil: cuadrado solo con ícono. Escritorio: mismo ancho que el otro botón
      flex: isVideoFilter && isMobile ? "none" : "0 0 44px",
      width: isVideoFilter && isMobile ? "100%" : "44px",
      minHeight: isVideoFilter && isMobile ? "38px" : "auto",
      padding: "0",
    }}
  >
    <i className="fa-brands fa-whatsapp" style={{ fontSize: "20px" }}></i>
  </a>
</div>
            </div>
          </div>
        ))}
      </div>
      </div>

    </div>  
  );

};

export default ProductCatalog;